// src/pages/customers/CustomersPage.jsx
import React from "react";
import Loader from "@components/Loader";
import CustomersFilter from "@components/customers/CustomersFilter";
import CustomersTable from "@components/customers/CustomersTable";
import { useCustomersQuery } from "@queries/customers";
import api from "@services/api";
import qs from "qs";
import "@styles/opencart-admin.css";

export default function CustomersPage() {
  const [filters, setFilters] = React.useState({ sort: "c.date_added", order: "DESC" });
  const [page, setPage] = React.useState(1);
  const pageSize = 20;
  const [isExporting, setIsExporting] = React.useState(false);

  const handleSort = (field) => {
    setFilters((prev) => {
      const isSameField = prev.sort === field;
      const nextOrder = isSameField && prev.order === "ASC" ? "DESC" : "ASC";
      return { ...prev, sort: field, order: nextOrder };
    });
    setPage(1);
  };

  const handleExportCsv = async () => {
    try {
      setIsExporting(true);
      const { data } = await api.get("/customers", {
        params: { ...filters, page: 1, page_size: 999999 },
        paramsSerializer: p => qs.stringify(p, { indices: false, skipNulls: true })
      });
      const fetchRows = data?.data || data || [];

      // Requested columns: Name, CID, Phone number, email
      const headers = ["Name", "CID", "Phone number", "Email"];
      const csvRows = fetchRows.map(c => {
         return [
           `"${String(c.name || c.firstname || "").replace(/"/g, '""')}"`,
           `"${String(c.customer_id || c.id || "").replace(/"/g, '""')}"`,
           `"${String(c.telephone || "").replace(/"/g, '""')}"`,
           `"${String(c.email || "").replace(/"/g, '""')}"`
         ].join(",");
      });

      const csvContent = [headers.join(","), ...csvRows].join("\n");
      const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" }); // include BOM for Excel
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", `customers_export.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error("Export failed", err);
      alert("Failed to export customers.");
    } finally {
      setIsExporting(false);
    }
  };

  const { data, isLoading, isFetching, isError, error } =
    useCustomersQuery(filters, page, pageSize);

  const rows = data?.data || [];
  const total = data?.meta?.total ?? rows.length;
  const pages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="oc-content">
      {(isLoading || isFetching) && <Loader />}

      <div className="oc-header">
        <h1>Customers</h1>
        <nav className="oc-bc">
          <span>Home</span><span className="sep">›</span><span>Customers</span>
        </nav>
      </div>

      <div className="oc-card oc-filter">
        <div className="title">Filter</div>
        <CustomersFilter
          value={filters}
          onChange={(f)=>{ setPage(1); setFilters((prev) => ({ ...prev, ...f })); }}
        />
      </div>

      <div className="oc-card">
        <div>
          <div className="oc-toolbar" style={{ marginBottom:12 }}>
            <div><strong>Customer List</strong></div>
            <button 
              className="oc-btn" 
              onClick={handleExportCsv} 
              disabled={isExporting}
              style={{ display: "flex", gap: "8px", alignItems: "center", backgroundColor: "var(--oc-success)", borderColor: "var(--oc-success)" }}
            >
              <i className="fa fa-download" />
              {isExporting ? "Exporting..." : "Export Customers (CSV)"}
            </button>
          </div>
          {isError && <div style={{ color:"#c00" }}>Error: {error.message}</div>}
          <CustomersTable 
            rows={rows} 
            sort={filters.sort} 
            order={filters.order} 
            onSort={handleSort} 
          />
        </div>
      </div>

      <div className="oc-card" style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        <div>Showing page <strong>{page}</strong> of <strong>{pages}</strong> ({total} total)</div>
        <div style={{ display:"flex", gap:8 }}>
          <button className="oc-btn ghost" disabled={page<=1} onClick={()=>setPage(1)}>« First</button>
          <button className="oc-btn ghost" disabled={page<=1} onClick={()=>setPage(p=>p-1)}>‹ Prev</button>
          <button className="oc-btn ghost" disabled={page>=pages} onClick={()=>setPage(p=>p+1)}>Next ›</button>
          <button className="oc-btn ghost" disabled={page>=pages} onClick={()=>setPage(pages)}>Last »</button>
        </div>
      </div>
    </div>
  );
}

